import { ethers } from 'ethers';
import CoinbaseWalletSDK from '@coinbase/wallet-sdk';

// ---------- Coinbase Wallet ----------
// Prefer the injected BROWSER EXTENSION (or already-installed dApp browser) so the
// user's real extension pops up to sign — not the SDK's hosted popup / mobile-scan
// flow. Only fall back to the SDK if no injected Coinbase provider is present.

const findInjectedCoinbase = () => {
  if (typeof window === 'undefined') return null;
  // Coinbase Wallet extension injects a dedicated handle:
  if (window.coinbaseWalletExtension) return window.coinbaseWalletExtension;
  // EIP-6963 / EIP-1193 multi-provider merge:
  const eth = window.ethereum;
  if (!eth) return null;
  if (eth.providers && Array.isArray(eth.providers)) {
    const cb = eth.providers.find((p) => p.isCoinbaseWallet || p.isCoinbaseBrowser);
    if (cb) return cb;
  }
  if (eth.isCoinbaseWallet || eth.isCoinbaseBrowser) return eth;
  return null;
};

let coinbaseSdkProvider = null;
const getCoinbaseSdkProvider = () => {
  if (!coinbaseSdkProvider) {
    const sdk = new CoinbaseWalletSDK({
      appName: 'CoinForge',
      appLogoUrl: 'https://images.unsplash.com/photo-1623227413711-25ee4388dae3?w=64',
      appChainIds: [1, 11155111, 8453, 84532],
    });
    // 'eoaOnly' avoids the smart-wallet passkey popup and behaves like a
    // classic wallet flow when the extension isn't installed.
    coinbaseSdkProvider = sdk.makeWeb3Provider({ options: 'eoaOnly' });
  }
  return coinbaseSdkProvider;
};

export const connectCoinbase = async () => {
  // 1. Extension first
  let eth = findInjectedCoinbase();
  // 2. Fallback: SDK (opens Coinbase-hosted flow) — only if extension not installed
  if (!eth) eth = getCoinbaseSdkProvider();

  const accounts = await eth.request({ method: 'eth_requestAccounts' });
  const provider = new ethers.BrowserProvider(eth);
  const network = await provider.getNetwork();
  return {
    walletType: 'coinbase',
    address: accounts[0],
    chainId: Number(network.chainId),
    provider,
    rawProvider: eth,
  };
};

export const disconnectCoinbase = () => {
  if (coinbaseSdkProvider && coinbaseSdkProvider.disconnect) {
    try { coinbaseSdkProvider.disconnect(); } catch (_) { /* ignore */ }
  }
  coinbaseSdkProvider = null;
};

// ---------- MetaMask ----------
export const connectMetaMask = async () => {
  if (!window.ethereum) {
    throw new Error('MetaMask not detected. Please install the MetaMask extension.');
  }
  // If multiple injected providers coexist, pick the MetaMask one so the
  // MetaMask extension popup (not any other injected wallet) opens.
  const eth = window.ethereum.providers
    ? window.ethereum.providers.find((p) => p.isMetaMask && !p.isCoinbaseWallet) || window.ethereum
    : window.ethereum;
  const accounts = await eth.request({ method: 'eth_requestAccounts' });
  const provider = new ethers.BrowserProvider(eth);
  const network = await provider.getNetwork();
  return {
    walletType: 'metamask',
    address: accounts[0],
    chainId: Number(network.chainId),
    provider,
    rawProvider: eth,
  };
};

export const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');
